/**
 * Debounced autosave.
 *
 * A Sales user should be able to type, close the laptop, and find their work
 * where they left it. The rules that make that true:
 *
 *  - Debounce: saves are coalesced, so typing a client name is one request.
 *  - In-flight coalescing: a change made while a save is running queues exactly
 *    one follow-up rather than a request per keystroke.
 *  - Revision tracking: every save carries the revision the client last saw, so
 *    a second tab is reported as a conflict instead of silently overwriting.
 *  - Flush on leave: a pending save is sent before the tab is hidden or closed.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../lib/api';

export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error' | 'conflict';

export interface AutosaveState<T> {
  status: SaveStatus;
  lastSavedAt: Date | null;
  error: string | null;
  /** Queue a change. Returns immediately; the save happens after the debounce. */
  save(value: T): void;
  /** Send any pending change now, e.g. before navigating away. */
  flush(): Promise<void>;
  /** Clear an error or conflict once the caller has reloaded. */
  reset(): void;
}

export interface AutosaveOptions<T> {
  /** Performs the write. Should throw ApiError on failure. */
  onSave(value: T, revision: number): Promise<{ revision: number }>;
  /** The revision currently held by the server, as last observed. */
  revision: number;
  debounceMs?: number;
  onConflict?(): void;
}

export function useAutosave<T>({
  onSave,
  revision,
  debounceMs = 800,
  onConflict,
}: AutosaveOptions<T>): AutosaveState<T> {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<T | null>(null);
  const inFlight = useRef(false);
  const revisionRef = useRef(revision);
  const mounted = useRef(true);

  // The caller owns the revision (it also changes on reload and on generate),
  // so mirror it rather than deriving our own.
  useEffect(() => {
    revisionRef.current = revision;
  }, [revision]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const flushNow = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    // A save is already running; it will pick up `pending` when it finishes.
    if (inFlight.current || pending.current === null) return;

    const value = pending.current;
    pending.current = null;
    inFlight.current = true;
    if (mounted.current) setStatus('saving');

    try {
      const result = await onSave(value, revisionRef.current);
      revisionRef.current = result.revision;
      if (mounted.current) {
        setStatus('saved');
        setLastSavedAt(new Date());
        setError(null);
      }
    } catch (caught) {
      if (mounted.current) {
        if (caught instanceof ApiError && caught.isConflict) {
          setStatus('conflict');
          setError('This proposal was changed elsewhere. Reload to continue.');
          onConflict?.();
        } else {
          setStatus('error');
          setError(
            caught instanceof ApiError ? caught.message : 'Could not save. Retrying shortly.',
          );
        }
      }
    } finally {
      inFlight.current = false;
      // A change arrived while we were saving: send exactly one follow-up.
      if (pending.current !== null && mounted.current) void flushNow();
    }
  }, [onSave, onConflict]);

  const save = useCallback(
    (value: T) => {
      pending.current = value;
      setStatus('pending');
      setError(null);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void flushNow(), debounceMs);
    },
    [flushNow, debounceMs],
  );

  // Don't lose the last keystroke when the user switches tab or closes it.
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === 'hidden') void flushNow();
    };
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', onHidden);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onHidden);
    };
  }, [flushNow]);

  // Warn before discarding an unsaved change on a hard navigation.
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (pending.current !== null || inFlight.current) event.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
  }, []);

  return { status, lastSavedAt, error, save, flush: flushNow, reset };
}
